<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BrandMark } from '@signage42/player-renderer';
import { publicPost, PlayerApiError } from '@/api/client';
import { writeTokens } from '@/api/tokenStore';
import type { DeviceTokens } from '@/lib/types';
import { BUILD_VERSION, PROVISIONED_PAIRING_CODE } from '@/main';
import { reloadBehindCover, updatePending } from '@/update/sw-update';
import { claimAgentEnrollment } from '@/telemetry/agent-claim';

const PAIRING_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 6;

const router = useRouter();
const route = useRoute();

// The screen-shows-a-code flow is the default: this device asks the API for a
// pairing request, displays the code, and polls until an admin claims it in
// the web UI. Typing a code here (the pre-reverse-pairing flow) survives as a
// fallback for anyone with a keyboard attached — start typing to reach it.
type Mode = 'auto' | 'request' | 'manual';
const mode = ref<Mode>('request');

// ---- request mode state ----
const displayCode = ref<string | null>(null);
const requestNotice = ref<string | null>(null);
const countdown = ref('');
let requestId: string | null = null;
let pollToken: string | null = null;
let requestExpiresAt = 0;
let pollIntervalMs = 4000;
let pollTimer: number | null = null;
let retryTimer: number | null = null;
let tickTimer: number | null = null;

// ---- manual mode state ----
const cells = ref<string[]>(Array.from({ length: CODE_LENGTH }, () => ''));
const cellRefs = ref<Array<HTMLInputElement | null>>(
  Array.from({ length: CODE_LENGTH }, () => null),
);
const submitting = ref(false);
const errorMessage = ref<string | null>(null);

const code = computed(() => cells.value.join(''));
const valid = computed(
  () =>
    code.value.length === CODE_LENGTH && [...code.value].every((c) => PAIRING_ALPHABET.includes(c)),
);

const displayCodeSpaced = computed(() =>
  displayCode.value ? [...displayCode.value].join(' ') : '',
);

interface PairingRequestCreated {
  id: string;
  code: string;
  pollToken: string;
  expiresAt: string;
  pollIntervalSeconds: number;
}

interface PairingPollResponse {
  status: 'pending' | 'ready';
  deviceId?: string;
  deviceToken?: string;
  refreshToken?: string;
  refreshExpiresAt?: string;
}

function clearTimers(): void {
  if (pollTimer) window.clearTimeout(pollTimer);
  if (retryTimer) window.clearTimeout(retryTimer);
  if (tickTimer) window.clearInterval(tickTimer);
  pollTimer = retryTimer = tickTimer = null;
}

function finishPairing(tokens: DeviceTokens): void {
  clearTimers();
  writeTokens(tokens);
  // The moment this browser is paired is the moment the agent beside it can be
  // enrolled — seconds after the admin's click, not at the next page load.
  void claimAgentEnrollment();
  void router.replace({ name: 'playback' });
}

async function startRequest(): Promise<void> {
  if (pollTimer) window.clearTimeout(pollTimer);
  try {
    const r = await publicPost<PairingRequestCreated>('/devices/pairing-requests', {});
    requestId = r.id;
    pollToken = r.pollToken;
    requestExpiresAt = Date.parse(r.expiresAt);
    pollIntervalMs = Math.max(2000, (r.pollIntervalSeconds || 4) * 1000);
    displayCode.value = r.code;
    requestNotice.value = null;
    schedulePoll();
    startTicker();
  } catch {
    // API unreachable — keep the last code visible (it may still be claimable)
    // and retry shortly.
    requestNotice.value = 'Cannot reach the API — retrying…';
    retryTimer = window.setTimeout(() => void startRequest(), 5000);
  }
}

function schedulePoll(): void {
  if (pollTimer) window.clearTimeout(pollTimer);
  pollTimer = window.setTimeout(() => void poll(), pollIntervalMs);
}

async function poll(): Promise<void> {
  if (!requestId || !pollToken) return;
  try {
    const r = await publicPost<PairingPollResponse>('/devices/pairing-requests/poll', {
      id: requestId,
      pollToken,
    });
    if (
      r.status === 'ready' &&
      r.deviceId &&
      r.deviceToken &&
      r.refreshToken &&
      r.refreshExpiresAt
    ) {
      finishPairing({
        deviceId: r.deviceId,
        deviceToken: r.deviceToken,
        refreshToken: r.refreshToken,
        refreshExpiresAt: r.refreshExpiresAt,
      });
      return;
    }
    schedulePoll();
  } catch (err) {
    if (err instanceof PlayerApiError && err.status === 410) {
      // Expired (or consumed under our feet) — show a fresh code.
      if (reloadInsteadOfRotating()) return;
      void startRequest();
      return;
    }
    // Transient network/server trouble: keep polling.
    schedulePoll();
  }
}

/**
 * The displayed code is about to be replaced anyway. If a new player build is
 * waiting, this is the one moment a reload costs nothing: never mid-claim,
 * because a reload regenerates the code an operator may be typing into the
 * admin UI at that very second (D14). Not while someone types a code here
 * either.
 */
function reloadInsteadOfRotating(): boolean {
  if (!updatePending() || mode.value !== 'request') return false;
  clearTimers();
  void reloadBehindCover();
  return true;
}

function startTicker(): void {
  if (tickTimer) window.clearInterval(tickTimer);
  tickTimer = window.setInterval(() => {
    const ms = requestExpiresAt - Date.now();
    if (ms <= 0) {
      countdown.value = '';
      if (tickTimer) window.clearInterval(tickTimer);
      tickTimer = null;
      if (reloadInsteadOfRotating()) return;
      void startRequest();
      return;
    }
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    countdown.value = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, 1000);
}

// ---- provisioned code (KIOSK_PAIRING_CODE → ?pairingCode=…) ----

async function tryProvisionedCode(raw: string): Promise<boolean> {
  const cleaned = [...raw.toUpperCase()].filter((c) => PAIRING_ALPHABET.includes(c)).join('');
  if (cleaned.length !== CODE_LENGTH) return false;
  mode.value = 'auto';
  try {
    const tokens = await publicPost<DeviceTokens>('/devices/pairing-codes/redeem', {
      code: cleaned,
    });
    finishPairing(tokens);
    return true;
  } catch (err) {
    if (err instanceof PlayerApiError) {
      requestNotice.value =
        'The provisioned pairing code was not accepted (expired or already used).';
    } else {
      requestNotice.value = 'Cannot reach the API. Check the network and try again.';
    }
    return false;
  }
}

// ---- manual entry (keyboard fallback) ----

function setCell(idx: number, raw: string): void {
  const upper = raw.toUpperCase();
  const filtered = [...upper].filter((c) => PAIRING_ALPHABET.includes(c)).join('');
  if (filtered.length === 0) {
    cells.value[idx] = '';
    return;
  }
  // Spread incoming chars across this cell and any to the right.
  let cursor = idx;
  for (const ch of filtered) {
    if (cursor >= CODE_LENGTH) break;
    cells.value[cursor] = ch;
    cursor++;
  }
  // Move focus to the next empty cell (or the last filled one if none).
  const nextEmpty = cells.value.findIndex((v, i) => i >= idx && v === '');
  const target = nextEmpty === -1 ? Math.min(cursor, CODE_LENGTH - 1) : nextEmpty;
  void nextTick(() => {
    cellRefs.value[target]?.focus();
    cellRefs.value[target]?.select();
  });
}

function onInput(idx: number, event: Event): void {
  const t = event.target as HTMLInputElement;
  setCell(idx, t.value);
}

function onKeyDown(idx: number, event: KeyboardEvent): void {
  if (event.key === 'Backspace' && cells.value[idx] === '' && idx > 0) {
    event.preventDefault();
    cells.value[idx - 1] = '';
    cellRefs.value[idx - 1]?.focus();
    cellRefs.value[idx - 1]?.select();
  } else if (event.key === 'ArrowLeft' && idx > 0) {
    event.preventDefault();
    cellRefs.value[idx - 1]?.focus();
    cellRefs.value[idx - 1]?.select();
  } else if (event.key === 'ArrowRight' && idx < CODE_LENGTH - 1) {
    event.preventDefault();
    cellRefs.value[idx + 1]?.focus();
    cellRefs.value[idx + 1]?.select();
  } else if (event.key === 'Enter') {
    void submit();
  }
}

function onPaste(event: ClipboardEvent): void {
  const text = event.clipboardData?.getData('text') ?? '';
  if (!text) return;
  event.preventDefault();
  setCell(0, text);
}

function focusFirstEmpty(): void {
  const target = cells.value.findIndex((v) => v === '');
  const idx = target === -1 ? 0 : target;
  cellRefs.value[idx]?.focus();
  cellRefs.value[idx]?.select();
}

async function submit(): Promise<void> {
  if (!valid.value || submitting.value) return;
  submitting.value = true;
  errorMessage.value = null;
  try {
    const tokens = await publicPost<DeviceTokens>('/devices/pairing-codes/redeem', {
      code: code.value,
    });
    finishPairing(tokens);
  } catch (err) {
    if (err instanceof PlayerApiError) {
      if (err.status === 410) errorMessage.value = 'This code has expired or already been used.';
      else if (err.status === 404) errorMessage.value = 'Unknown pairing code.';
      else errorMessage.value = `Pairing failed (HTTP ${err.status}).`;
    } else {
      errorMessage.value = 'Cannot reach the API. Check the network and try again.';
    }
    cells.value = Array.from({ length: CODE_LENGTH }, () => '');
    void nextTick(() => focusFirstEmpty());
  } finally {
    submitting.value = false;
  }
}

// A keyboard is the exception on a kiosk, but when one is attached, typing
// switches to manual entry; Escape returns to the displayed code. Polling for
// the displayed code keeps running underneath either view.
function onWindowKeyDown(event: KeyboardEvent): void {
  if (mode.value === 'manual') {
    if (event.key === 'Escape') {
      mode.value = 'request';
      errorMessage.value = null;
    }
    return;
  }
  if (mode.value !== 'request') return;
  if (event.target instanceof HTMLInputElement) return;
  const ch = event.key.length === 1 ? event.key.toUpperCase() : '';
  if (ch && PAIRING_ALPHABET.includes(ch)) {
    mode.value = 'manual';
    void nextTick(() => setCell(0, ch));
  }
}

onMounted(async () => {
  window.addEventListener('keydown', onWindowKeyDown);
  const fromQuery = route.query.pairingCode;
  const provisioned =
    typeof fromQuery === 'string' && fromQuery ? fromQuery : PROVISIONED_PAIRING_CODE;
  if (provisioned) {
    const paired = await tryProvisionedCode(provisioned);
    if (paired) return;
  }
  mode.value = 'request';
  void startRequest();
});

onUnmounted(() => {
  window.removeEventListener('keydown', onWindowKeyDown);
  clearTimers();
});
</script>

<template>
  <main class="pair">
    <!-- Animated backdrop -->
    <div class="backdrop">
      <div class="aurora a1" />
      <div class="aurora a2" />
      <div class="aurora a3" />
      <div class="grain" aria-hidden="true" />
      <svg class="thread" viewBox="0 0 1600 900" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stop-color="rgba(226,180,78,0)" />
            <stop offset="50%" stop-color="rgba(226,180,78,0.45)" />
            <stop offset="100%" stop-color="rgba(226,180,78,0)" />
          </linearGradient>
        </defs>
        <path
          d="M -100 480 Q 400 460 800 510 T 1700 470"
          fill="none"
          stroke="url(#g)"
          stroke-width="0.6"
        />
      </svg>
    </div>

    <section class="card">
      <header class="mark">
        <BrandMark class="brand-mark" />
        <span class="mark-text">player</span>
      </header>

      <!-- Provisioned code being redeemed -->
      <template v-if="mode === 'auto'">
        <h1>Pairing this screen…</h1>
        <div class="auto-spinner">
          <span class="spinner spinner-lg" aria-hidden="true" />
        </div>
      </template>

      <!-- Default: show a code, admin claims it remotely -->
      <template v-else-if="mode === 'request'">
        <h1>Pair this screen</h1>
        <p class="hint">
          In the admin app open <em>Screens</em>, choose this screen,<br />
          press <em>Pair</em> and enter the code below.
        </p>

        <div class="code-display" role="status" aria-label="Pairing code">
          <span v-if="displayCode">{{ displayCodeSpaced }}</span>
          <span v-else class="spinner spinner-lg" aria-hidden="true" />
        </div>
        <p class="countdown">{{ countdown ? `A new code appears in ${countdown}` : '' }}&nbsp;</p>

        <p v-if="requestNotice" class="error" role="alert">{{ requestNotice }}</p>

        <p class="kbd-hint">Keyboard attached? Start typing to enter a code instead.</p>
      </template>

      <!-- Fallback: type a code issued in the admin -->
      <template v-else>
        <h1>Pair this screen</h1>
        <p class="hint">
          Issue a pairing code in the admin app (Screens → <em>Pair</em>),<br />
          then enter the six characters below.
        </p>

        <form class="form" @submit.prevent="submit" @paste="onPaste">
          <div class="cells" role="group" aria-label="Pairing code">
            <input
              v-for="(value, idx) in cells"
              :key="idx"
              :ref="(el) => (cellRefs[idx] = el as HTMLInputElement | null)"
              class="cell"
              :class="{ filled: value !== '' }"
              inputmode="text"
              autocapitalize="characters"
              autocomplete="off"
              spellcheck="false"
              maxlength="1"
              :aria-label="`Character ${idx + 1} of ${CODE_LENGTH}`"
              :value="value"
              @input="(e) => onInput(idx, e)"
              @keydown="(e) => onKeyDown(idx, e)"
              @focus="(e) => (e.target as HTMLInputElement).select()"
            />
          </div>

          <button type="submit" class="primary" :disabled="!valid || submitting">
            <span v-if="!submitting">Pair</span>
            <span v-else class="spinner" aria-hidden="true" />
            <span v-if="submitting" class="sr">Pairing…</span>
          </button>
        </form>

        <p v-if="errorMessage" class="error" role="alert">{{ errorMessage }}</p>

        <p class="kbd-hint">Press Escape to show this screen's own code again.</p>
      </template>

      <footer class="footer">
        <span class="dot" />
        <span class="footer-text">Awaiting handshake</span>
        <span class="version">build {{ BUILD_VERSION }}</span>
      </footer>
    </section>
  </main>
</template>

<style scoped>
/* ===================== Stage / backdrop ===================== */
.pair {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: #0a0807;
  overflow: hidden;
  color: #f6eedc;
  font-family: 'Iowan Old Style', Georgia, 'Times New Roman', serif;
}
.backdrop {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}
.aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
  mix-blend-mode: screen;
  will-change: transform, opacity;
}
.aurora.a1 {
  top: -10%;
  left: -10%;
  width: 60vmin;
  height: 60vmin;
  background: radial-gradient(circle, rgba(226, 180, 78, 0.42), transparent 70%);
  animation: drift1 28s ease-in-out infinite alternate;
}
.aurora.a2 {
  bottom: -15%;
  right: -10%;
  width: 70vmin;
  height: 70vmin;
  background: radial-gradient(circle, rgba(245, 184, 160, 0.18), transparent 70%);
  animation: drift2 36s ease-in-out infinite alternate;
}
.aurora.a3 {
  top: 35%;
  left: 60%;
  width: 40vmin;
  height: 40vmin;
  background: radial-gradient(circle, rgba(246, 238, 220, 0.1), transparent 70%);
  animation: drift3 22s ease-in-out infinite alternate;
}

@keyframes drift1 {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.55;
  }
  100% {
    transform: translate(20vmin, 12vmin) scale(1.15);
    opacity: 0.7;
  }
}
@keyframes drift2 {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.5;
  }
  100% {
    transform: translate(-18vmin, -10vmin) scale(1.1);
    opacity: 0.65;
  }
}
@keyframes drift3 {
  0% {
    transform: translate(0, 0);
    opacity: 0.4;
  }
  100% {
    transform: translate(-12vmin, 14vmin);
    opacity: 0.65;
  }
}

/* Subtle film grain via SVG noise */
.grain {
  position: absolute;
  inset: 0;
  opacity: 0.45;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.96 0 0 0 0 0.93 0 0 0 0 0.86 0 0 0 0.06 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}

/* Slow thread of light arching across the stage */
.thread {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  animation: thread-fade 14s ease-in-out infinite alternate;
}
@keyframes thread-fade {
  0% {
    opacity: 0.3;
    transform: translateY(-2%);
  }
  100% {
    opacity: 0.9;
    transform: translateY(2%);
  }
}

/* ===================== Card ===================== */
.card {
  position: relative;
  z-index: 1;
  width: min(520px, 92vw);
  padding: 56px 48px 36px;
  background: linear-gradient(180deg, rgba(246, 238, 220, 0.06), rgba(246, 238, 220, 0.02));
  border: 1px solid rgba(246, 238, 220, 0.13);
  box-shadow:
    inset 0 1px 0 0 rgba(246, 238, 220, 0.08),
    0 30px 80px -20px rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  text-align: center;
  animation: card-rise 700ms cubic-bezier(0.2, 0.7, 0.2, 1) both;
}
.card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(226, 180, 78, 0.5), transparent) top / 100%
    1px no-repeat;
  animation: rule-sweep 6s ease-in-out infinite;
}
@keyframes card-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes rule-sweep {
  0%,
  100% {
    background-position-x: -50%;
    opacity: 0.4;
  }
  50% {
    background-position-x: 150%;
    opacity: 1;
  }
}

/* ===================== Header / mark ===================== */
.mark {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 36px;
}
.brand-mark {
  font-size: 20px;
  color: #f6eedc;
}
.mark-text {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(246, 238, 220, 0.55);
}

h1 {
  font-family: 'Iowan Old Style', 'Times New Roman', Georgia, serif;
  font-weight: 400;
  font-style: italic;
  font-size: 36px;
  line-height: 1.05;
  letter-spacing: -0.015em;
  margin: 0 0 12px;
  color: #f6eedc;
}
.hint {
  margin: 0 0 32px;
  color: rgba(246, 238, 220, 0.65);
  font-size: 14px;
  line-height: 1.6;
  font-family: 'Iowan Old Style', Georgia, serif;
}
.hint em {
  color: #e2b44e;
  font-style: italic;
}

/* ===================== Displayed pairing code ===================== */
.code-display {
  display: grid;
  place-items: center;
  min-height: 96px;
  margin: 0 auto 8px;
  max-width: 420px;
  padding: 18px 12px;
  background: rgba(226, 180, 78, 0.05);
  border: 1px solid rgba(226, 180, 78, 0.35);
  border-radius: 2px;
  color: #f6eedc;
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, monospace;
  font-size: 44px;
  font-weight: 600;
  letter-spacing: 0.1em;
  /* letter-spacing trails the last glyph; indent rebalances the box */
  text-indent: 0.1em;
  white-space: nowrap;
}
.countdown {
  margin: 0 0 20px;
  min-height: 1.4em;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(246, 238, 220, 0.45);
  font-variant-numeric: tabular-nums;
}
.kbd-hint {
  margin: 24px 0 0;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(246, 238, 220, 0.35);
}
.auto-spinner {
  display: grid;
  place-items: center;
  min-height: 96px;
  margin-bottom: 20px;
}

/* ===================== Form ===================== */
.form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
}
.cells {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  width: 100%;
  max-width: 420px;
}
.cell {
  width: 100%;
  aspect-ratio: 4 / 5;
  background: rgba(246, 238, 220, 0.03);
  border: 1px solid rgba(246, 238, 220, 0.16);
  border-radius: 2px;
  color: #f6eedc;
  font-family: ui-monospace, 'SF Mono', SFMono-Regular, monospace;
  font-size: 28px;
  font-weight: 500;
  text-align: center;
  outline: none;
  caret-color: #e2b44e;
  transition:
    border-color 220ms ease,
    background 220ms ease,
    transform 220ms ease;
}
.cell:hover {
  border-color: rgba(246, 238, 220, 0.3);
}
.cell:focus {
  border-color: #e2b44e;
  background: rgba(226, 180, 78, 0.06);
  box-shadow:
    0 0 0 3px rgba(226, 180, 78, 0.12),
    inset 0 -2px 0 0 #e2b44e;
}
.cell.filled {
  border-color: rgba(226, 180, 78, 0.35);
  background: rgba(226, 180, 78, 0.04);
}

/* ===================== Button ===================== */
.primary {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 180px;
  padding: 14px 32px;
  background: #e2b44e;
  color: #0a0807;
  border: 0;
  border-radius: 2px;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  cursor: pointer;
  overflow: hidden;
  transition:
    background 200ms ease,
    transform 200ms ease,
    opacity 200ms ease;
}
.primary::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent);
  transform: translateX(-100%);
  transition: transform 600ms ease;
}
.primary:not(:disabled):hover {
  background: #f5e3a1;
}
.primary:not(:disabled):hover::after {
  transform: translateX(100%);
}
.primary:disabled {
  opacity: 0.32;
  cursor: not-allowed;
  background: rgba(246, 238, 220, 0.18);
  color: rgba(246, 238, 220, 0.55);
}
.spinner {
  width: 14px;
  height: 14px;
  border: 1.5px solid rgba(10, 8, 7, 0.25);
  border-top-color: #0a0807;
  border-radius: 50%;
  animation: spin 800ms linear infinite;
}
/* Larger variant used on the dark card, outside the yellow button. */
.spinner-lg {
  width: 28px;
  height: 28px;
  border: 2px solid rgba(246, 238, 220, 0.2);
  border-top-color: #e2b44e;
}
.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===================== Error ===================== */
.error {
  margin: 20px 0 0;
  padding: 10px 14px;
  background: rgba(245, 184, 160, 0.08);
  border-left: 2px solid #f5b8a0;
  color: #f5b8a0;
  font-family: 'Iowan Old Style', Georgia, serif;
  font-size: 13.5px;
  line-height: 1.5;
  text-align: left;
  animation: shake 360ms cubic-bezier(0.36, 0.07, 0.19, 0.97);
}
@keyframes shake {
  10%,
  90% {
    transform: translateX(-1px);
  }
  20%,
  80% {
    transform: translateX(2px);
  }
  30%,
  50%,
  70% {
    transform: translateX(-3px);
  }
  40%,
  60% {
    transform: translateX(3px);
  }
}

/* ===================== Footer ===================== */
.footer {
  margin-top: 36px;
  padding-top: 20px;
  border-top: 1px solid rgba(246, 238, 220, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(246, 238, 220, 0.4);
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e2b44e;
  box-shadow: 0 0 0 0 rgba(226, 180, 78, 0.6);
  animation: pulse 2.4s ease-in-out infinite;
}
.footer-text {
  flex: 1;
  text-align: left;
}
.version {
  color: rgba(246, 238, 220, 0.3);
  font-size: 9.5px;
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(226, 180, 78, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(226, 180, 78, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(226, 180, 78, 0);
  }
}

/* ===================== Reduced motion ===================== */
@media (prefers-reduced-motion: reduce) {
  .aurora,
  .thread,
  .card::before,
  .dot,
  .spinner {
    animation: none !important;
  }
  .card {
    animation: none;
  }
}

/* ===================== Smaller stages ===================== */
@media (max-width: 540px) {
  .card {
    padding: 40px 28px 28px;
  }
  h1 {
    font-size: 28px;
  }
  .cell {
    font-size: 22px;
  }
  .code-display {
    font-size: 38px;
  }
}
</style>
